// cloudinaryService.js
// Service module for handling Cloudinary image uploads

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
const CLOUDINARY_API_KEY = process.env.REACT_APP_CLOUDINARY_API_KEY;
const CLOUDINARY_API_SECRET = process.env.REACT_APP_CLOUDINARY_API_SECRET; // Only use if doing signed uploads from backend

// Debug logging
console.log('Cloudinary Config Check:', {
  cloudName: CLOUDINARY_CLOUD_NAME ? '✓ Set' : '✗ Missing',
  uploadPreset: CLOUDINARY_UPLOAD_PRESET ? '✓ Set' : '✗ Missing',
  cloudNameValue: CLOUDINARY_CLOUD_NAME || 'NOT SET',
  uploadPresetValue: CLOUDINARY_UPLOAD_PRESET || 'NOT SET'
});

// Cloudinary upload URL - build dynamically to avoid undefined in URL
const getUploadUrl = () => {
  if (!CLOUDINARY_CLOUD_NAME) {
    console.error('CLOUDINARY_CLOUD_NAME is not defined');
    return null;
  }
  return `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`;
};

// Service object for Cloudinary operations
const cloudinaryService = {
  /**
   * Upload a single image to Cloudinary
   * @param {File} file - The image file to upload
   * @param {Object} options - Upload options (folder, tags, etc.)
   * @returns {Promise<Object>} - Cloudinary response with image details
   */
  uploadImage: async (file, options = {}) => {
    try {
      // Validate environment variables
      if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
        console.error('Cloudinary configuration is missing:', {
          cloudName: CLOUDINARY_CLOUD_NAME || 'MISSING',
          uploadPreset: CLOUDINARY_UPLOAD_PRESET || 'MISSING'
        });
        throw new Error('Cloudinary configuration is missing. Please check your environment variables.');
      }

      const uploadUrl = getUploadUrl();
      if (!uploadUrl) {
        throw new Error('Cannot generate upload URL - Cloudinary not configured');
      }

      console.log('Starting Cloudinary upload with:', {
        url: uploadUrl,
        preset: CLOUDINARY_UPLOAD_PRESET,
        fileName: file.name,
        fileSize: file.size
      });

      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
      
      // Add optional parameters (only those allowed for unsigned uploads)
      if (options.folder) {
        formData.append('folder', options.folder);
      }
      
      if (options.tags) {
        formData.append('tags', Array.isArray(options.tags) ? options.tags.join(',') : options.tags);
      }
      
      if (options.context) {
        formData.append('context', options.context);
      }
      
      // NOTE: 'eager' parameter is NOT allowed for unsigned uploads
      // Transformations will be done on-the-fly via URL parameters instead

      // Perform the upload
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      const responseText = await response.text();
      console.log('Cloudinary response status:', response.status);
      
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse Cloudinary response:', responseText);
        throw new Error('Invalid response from Cloudinary');
      }

      if (!response.ok) {
        console.error('Cloudinary upload failed:', data);
        
        // Provide more helpful error messages
        if (data.error?.message?.includes('preset')) {
          console.error('❌ Upload preset issue detected!');
          console.error('Please create an UNSIGNED upload preset in your Cloudinary dashboard:');
          console.error('1. Go to: https://console.cloudinary.com/settings/upload');
          console.error('2. Click "Add upload preset"');
          console.error('3. Set Signing Mode to "Unsigned"');
          console.error('4. Save and use the preset name in your .env.local file');
        }
        
        throw new Error(data.error?.message || `Upload failed with status ${response.status}`);
      }

      console.log('Cloudinary upload successful:', data.public_id);
      
      // Return formatted response
      return {
        success: true,
        data: {
          id: data.public_id,
          url: data.secure_url,
          // Generate thumbnail URL on-the-fly instead of using eager transformations
          thumbnailUrl: cloudinaryService.getThumbnailUrl(data.secure_url, 200),
          format: data.format,
          width: data.width,
          height: data.height,
          bytes: data.bytes,
          publicId: data.public_id,
          createdAt: data.created_at,
          originalFilename: data.original_filename,
          resourceType: data.resource_type,
          type: data.type,
          etag: data.etag,
          placeholder: data.placeholder,
          cloudinaryData: data // Keep full response for reference
        }
      };
    } catch (error) {
      console.error('Error uploading to Cloudinary:', error);
      return {
        success: false,
        error: error.message || 'Failed to upload image'
      };
    }
  },

  /**
   * Upload multiple images to Cloudinary
   * @param {Array<File>} files - Array of image files to upload
   * @param {Object} options - Upload options
   * @param {Function} onProgress - Progress callback for each file
   * @returns {Promise<Array>} - Array of upload results
   */
  uploadMultipleImages: async (files, options = {}, onProgress) => {
    const results = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Call progress callback if provided
      if (onProgress) {
        onProgress({
          current: i + 1,
          total: files.length,
          percentage: Math.round(((i + 1) / files.length) * 100),
          fileName: file.name
        });
      }
      
      const result = await cloudinaryService.uploadImage(file, options);
      results.push(result);
    }
    
    return results;
  },

  /**
   * Delete an image from Cloudinary (requires backend endpoint for signed request)
   * @param {string} publicId - The public ID of the image to delete
   * @returns {Promise<Object>} - Deletion result
   */
  deleteImage: async (publicId) => {
    try {
      // Note: Deletion requires a signed request, so this should be done through your backend
      // This is a placeholder for the API call to your backend
      const response = await fetch('/api/cloudinary/delete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ publicId })
      });

      if (!response.ok) {
        throw new Error('Failed to delete image');
      }

      return {
        success: true,
        message: 'Image deleted successfully'
      };
    } catch (error) {
      console.error('Error deleting from Cloudinary:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete image'
      };
    }
  },

  /**
   * Generate a transformation URL for an uploaded image
   * @param {string} publicId - The public ID of the image
   * @param {Object} transformations - Transformation options
   * @returns {string} - Transformed image URL
   */
  getTransformationUrl: (publicId, transformations = {}) => {
    const baseUrl = `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload`;
    
    // Build transformation string
    const transformArray = [];
    
    if (transformations.width) {
      transformArray.push(`w_${transformations.width}`);
    }
    
    if (transformations.height) {
      transformArray.push(`h_${transformations.height}`);
    }
    
    if (transformations.crop) {
      transformArray.push(`c_${transformations.crop}`);
    }
    
    if (transformations.quality) {
      transformArray.push(`q_${transformations.quality}`);
    }
    
    if (transformations.format) {
      transformArray.push(`f_${transformations.format}`);
    }
    
    if (transformations.effect) {
      transformArray.push(`e_${transformations.effect}`);
    }
    
    const transformString = transformArray.length > 0 
      ? transformArray.join(',') + '/' 
      : '';
    
    return `${baseUrl}/${transformString}${publicId}`;
  },

  /**
   * Generate a thumbnail URL for an image
   * @param {string} url - Original image URL
   * @param {number} size - Thumbnail size (width and height)
   * @returns {string} - Thumbnail URL
   */
  getThumbnailUrl: (url, size = 200) => {
    if (!url || !url.includes('cloudinary.com')) {
      return url;
    }
    
    // Insert transformation parameters into the URL
    const parts = url.split('/upload/');
    if (parts.length === 2) {
      return `${parts[0]}/upload/w_${size},h_${size},c_thumb,g_face/${parts[1]}`;
    }
    
    return url;
  },

  /**
   * Validate if Cloudinary is properly configured
   * @returns {boolean} - True if configured, false otherwise
   */
  isConfigured: () => {
    return !!(CLOUDINARY_CLOUD_NAME && CLOUDINARY_UPLOAD_PRESET);
  },

  /**
   * Get configuration status
   * @returns {Object} - Configuration status details
   */
  getConfigStatus: () => {
    return {
      cloudName: !!CLOUDINARY_CLOUD_NAME,
      uploadPreset: !!CLOUDINARY_UPLOAD_PRESET,
      apiKey: !!CLOUDINARY_API_KEY,
      configured: !!(CLOUDINARY_CLOUD_NAME && CLOUDINARY_UPLOAD_PRESET)
    };
  }
};

export default cloudinaryService;